import React from "react";
import { ErrorMessage } from "formik";
import clsx from "clsx";
import {
  Card,
  CardContent,
  Box,
  Typography,
  Stack,
  TextField,
  FormGroup,
  Switch,
  ButtonBase,
  Collapse,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { useStyles } from "./styles";

const getCollapsedIntegrations = () => ({
  instagram: false,
  whatsapp: false,
  messenger: false,
  phorest: false,
});

const PHOREST_FIELD_NAMES = [
  "integrationsPhorestRegion",
  "integrationsPhorestBusinessId",
  "integrationsPhorestBranchId",
  "integrationsPhorestCurrency",
  "integrationsPhorestApiUsername",
  "integrationsPhorestApiPassword",
];

const MESSENGER_FIELD_NAMES = [
  "integrationsMessengerPageAccessToken",
];

function IntegrationCard({
  title,
  section,
  checked,
  onSwitchChange,
  switchName,
  switchDisabled = false,
  expanded,
  onToggle,
  canExpand,
  children,
  classes,
}) {
  const contentId = `${section}-integration-content`;

  return (
    <Card variant="outlined" sx={{ backgroundColor: "#f5f5f5", borderRadius: 1 }}>
      <CardContent sx={{ pt: 2.5, pb: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <ButtonBase
            aria-controls={contentId}
            aria-expanded={expanded}
            aria-label={`${expanded ? "Collapse" : "Expand"} ${title}`}
            disabled={!canExpand}
            onClick={onToggle}
            sx={{
              alignItems: "center",
              borderRadius: 0.5,
              display: "flex",
              flex: 1,
              justifyContent: "flex-start",
              minWidth: 0,
              px: 0.5,
              py: 0.5,
              textAlign: "left",
              "&.Mui-disabled": { opacity: 1 },
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0 }}>
              {title}
            </Typography>
            {expanded ? (
              <ExpandLessIcon aria-hidden="true" fontSize="small" sx={{ ml: 0.5 }} />
            ) : (
              <ExpandMoreIcon aria-hidden="true" fontSize="small" sx={{ ml: 0.5 }} />
            )}
          </ButtonBase>
          <FormGroup row className={classes.toggle_container}>
            <Switch
              checked={checked}
              onChange={onSwitchChange}
              name={switchName}
              color="primary"
              disabled={switchDisabled}
              inputProps={{ "aria-label": `Enable ${title}` }}
            />
          </FormGroup>
        </Box>
        <Collapse in={expanded && canExpand} timeout="auto" unmountOnExit>
          <Box id={contentId}>{children}</Box>
        </Collapse>
      </CardContent>
    </Card>
  );
}

function SocialTokenFields({
  values,
  handleChange,
  handleBlur,
  errors,
  touched,
  isLimitedMode = false,
  showInstagramToken = values.instagramDM === true,
  showWhatsAppToken = values.whatsAppDm === true,
  showMessengerSection = false,
  showMessengerConfiguration = values.integrationsMessengerEnabled === true,
  messengerLoading = false,
  messengerLoadError = false,
  showPhorestSection = false,
  showPhorestConfiguration = values.integrationsPhorestEnabled === true,
  phorestLoading = false,
  phorestLoadError = false,
  readOnly = false,
  showValidationErrors = false,
  integrationStateKey = null,
}) {
  const classes = useStyles();
  const [expandedIntegrations, setExpandedIntegrations] = React.useState(
    getCollapsedIntegrations
  );

  React.useEffect(() => {
    setExpandedIntegrations(getCollapsedIntegrations());
  }, [integrationStateKey]);

  const hasInstagramValidationError =
    showValidationErrors &&
    showInstagramToken &&
    Boolean(errors.instagramAccessToken);
  const hasWhatsAppValidationError =
    showValidationErrors &&
    showWhatsAppToken &&
    Boolean(errors.whatsAppAccessToken);
  const hasPhorestValidationError =
    showValidationErrors &&
    showPhorestConfiguration &&
    PHOREST_FIELD_NAMES.some((fieldName) => Boolean(errors[fieldName]));
  const hasMessengerValidationError =
    showValidationErrors &&
    showMessengerConfiguration &&
    MESSENGER_FIELD_NAMES.some((fieldName) => Boolean(errors[fieldName]));

  React.useEffect(() => {
    if (
      !hasInstagramValidationError &&
      !hasWhatsAppValidationError &&
      !hasMessengerValidationError &&
      !hasPhorestValidationError
    ) {
      return;
    }

    setExpandedIntegrations((current) => {
      const next = {
        ...current,
        instagram: current.instagram || hasInstagramValidationError,
        whatsapp: current.whatsapp || hasWhatsAppValidationError,
        messenger: current.messenger || hasMessengerValidationError,
        phorest: current.phorest || hasPhorestValidationError,
      };

      return next.instagram === current.instagram &&
        next.whatsapp === current.whatsapp &&
        next.messenger === current.messenger &&
        next.phorest === current.phorest
        ? current
        : next;
    });
  }, [
    hasInstagramValidationError,
    hasMessengerValidationError,
    hasPhorestValidationError,
    hasWhatsAppValidationError,
  ]);

  const toggleIntegration = React.useCallback((section) => {
    setExpandedIntegrations((current) => ({
      ...current,
      [section]: !current[section],
    }));
  }, []);

  const handleIntegrationSwitchChange = React.useCallback(
    (section) => (event) => {
      setExpandedIntegrations((current) => ({
        ...current,
        [section]: event.target.checked,
      }));
      handleChange(event);
    },
    [handleChange]
  );

  const renderTokenField = (
    name,
    label,
    { type = "text", placeholder = "", autoComplete } = {}
  ) => (
    <>
      <TextField
        label={label}
        name={name}
        type={type}
        placeholder={placeholder}
        autoComplete={autoComplete}
        error={Boolean(errors[name] && (touched[name] || showValidationErrors))}
        className={clsx(
          classes.textfield,
          errors[name] && touched[name] ? classes.inputError : classes.inputNoError
        )}
        onChange={handleChange}
        onBlur={handleBlur}
        value={values[name]}
        InputProps={{ readOnly }}
      />
      <ErrorMessage name={name} component="span" className={classes.error} />
    </>
  );

  if (isLimitedMode) {
    return (
      <>
        {showInstagramToken && (
          <Card variant="outlined" sx={{ backgroundColor: "#f5f5f5", borderRadius: 1 }}>
            <CardContent sx={{ pt: 2.5 }}>
              <Stack spacing={2}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0 }}>
                  Instagram Access Token
                </Typography>
                {renderTokenField("instagramAccessToken", "Instagram Access Token")}
              </Stack>
            </CardContent>
          </Card>
        )}

        {showWhatsAppToken && (
          <Card variant="outlined" sx={{ backgroundColor: "#f5f5f5", borderRadius: 1 }}>
            <CardContent sx={{ pt: 2.5 }}>
              <Stack spacing={2}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0 }}>
                  WhatsApp Access Token
                </Typography>
                {renderTokenField("whatsAppAccessToken", "WhatsApp Access Token")}
              </Stack>
            </CardContent>
          </Card>
        )}
      </>
    );
  }

  return (
    <>
      <IntegrationCard
        title="Instagram DM"
        section="instagram"
        checked={values.instagramDM}
        onSwitchChange={handleIntegrationSwitchChange("instagram")}
        switchName="instagramDM"
        switchDisabled={readOnly}
        expanded={expandedIntegrations.instagram}
        onToggle={() => toggleIntegration("instagram")}
        canExpand={showInstagramToken}
        classes={classes}
      >
        <Stack sx={{ mt: 2 }}>
          {renderTokenField("instagramAccessToken", "Instagram Access Token")}
        </Stack>
      </IntegrationCard>

      <IntegrationCard
        title="WhatsApp DM"
        section="whatsapp"
        checked={values.whatsAppDm}
        onSwitchChange={handleIntegrationSwitchChange("whatsapp")}
        switchName="whatsAppDm"
        switchDisabled={readOnly}
        expanded={expandedIntegrations.whatsapp}
        onToggle={() => toggleIntegration("whatsapp")}
        canExpand={showWhatsAppToken}
        classes={classes}
      >
        <Stack sx={{ mt: 2 }}>
          {renderTokenField("whatsAppAccessToken", "WhatsApp Access Token")}
        </Stack>
      </IntegrationCard>

      {showMessengerSection && (
        <IntegrationCard
          title="Facebook Messenger"
          section="messenger"
          checked={values.integrationsMessengerEnabled}
          onSwitchChange={handleIntegrationSwitchChange("messenger")}
          switchName="integrationsMessengerEnabled"
          switchDisabled={readOnly || messengerLoading || messengerLoadError}
          expanded={expandedIntegrations.messenger}
          onToggle={() => toggleIntegration("messenger")}
          canExpand={
            messengerLoading || messengerLoadError || showMessengerConfiguration
          }
          classes={classes}
        >
          {messengerLoading && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Loading Facebook Messenger settings...
            </Typography>
          )}
          {messengerLoadError && (
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
              Facebook Messenger settings are temporarily unavailable.
            </Typography>
          )}
          {!messengerLoading && !messengerLoadError && showMessengerConfiguration && (
            <Stack spacing={2} sx={{ mt: 2 }}>
              {renderTokenField(
                "integrationsMessengerPageAccessToken",
                "Facebook Page Access Token",
                {
                  type: "password",
                  autoComplete: "new-password",
                  placeholder: values.integrationsMessengerPageAccessTokenConfigured
                    ? "Leave blank to keep the stored token"
                    : "",
                }
              )}
            </Stack>
          )}
        </IntegrationCard>
      )}

      {showPhorestSection && (
        <IntegrationCard
          title="Phorest"
          section="phorest"
          checked={values.integrationsPhorestEnabled}
          onSwitchChange={handleIntegrationSwitchChange("phorest")}
          switchName="integrationsPhorestEnabled"
          switchDisabled={readOnly || phorestLoading || phorestLoadError}
          expanded={expandedIntegrations.phorest}
          onToggle={() => toggleIntegration("phorest")}
          canExpand={
            phorestLoading || phorestLoadError || showPhorestConfiguration
          }
          classes={classes}
        >
          {phorestLoading && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Loading Phorest settings...
            </Typography>
          )}
          {phorestLoadError && (
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
              Phorest settings are temporarily unavailable.
            </Typography>
          )}
          {!phorestLoading && !phorestLoadError && showPhorestConfiguration && (
            <Stack spacing={2} sx={{ mt: 2 }}>
              {renderTokenField("integrationsPhorestRegion", "Region")}
              {renderTokenField("integrationsPhorestBusinessId", "Phorest Business ID")}
              {renderTokenField("integrationsPhorestBranchId", "Phorest Branch ID")}
              {renderTokenField("integrationsPhorestCurrency", "Currency")}
              {renderTokenField("integrationsPhorestApiUsername", "API Username")}
              {renderTokenField("integrationsPhorestApiPassword", "API Password", {
                type: "password",
                autoComplete: "new-password",
                placeholder: values.integrationsPhorestApiPasswordConfigured
                  ? "Leave blank to keep the stored password"
                  : "",
              })}
            </Stack>
          )}
        </IntegrationCard>
      )}
    </>
  );
}

export default SocialTokenFields;
